import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './AboutUsLeadershipForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsLeadershipForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ items: [] });
  const [originalData, setOriginalData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    loadMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsLeadershipForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let leadershipSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          leadershipSection = result.data.find(s => s.section_type === 'about-us-leadership-shell');
        }
      }
      
      if (!leadershipSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            leadershipSection = result.data.find(s => s.section_type === 'about-us-leadership-shell');
          }
        }
      }
      
      if (!leadershipSection) {
        throw new Error('Leadership section not found');
      }

      setSectionId(leadershipSection.id);

      let newFormData = { items: [] };

      if (leadershipSection.content_published) {
        let published = leadershipSection.content_published;
        if (typeof published === 'string') published = JSON.parse(published);
        newFormData.items = published.en?.items || [];
      }

      setFormData(newFormData);
      setOriginalData(newFormData);
    } catch (err) {
      console.error('[AboutUsLeadershipForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: '', role: '', bio: '', photo: null }]
    }));
  };

  const handleDeleteItem = (itemIndex) => {
    const itemToDelete = originalData.items[itemIndex];
    
    if (itemToDelete) {
      handleSubmitItemDelete(itemIndex, itemToDelete);
    }
    
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== itemIndex)
    }));
  };

  const handleItemChange = (field, value) => {
    setModalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    if (editingIndex === null && editingIndex !== 0) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [editingIndex]: true }));
    setImageProgress(prev => ({ ...prev, [editingIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [editingIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                handleItemChange('photo', mediaId);
                toast.success('✓ Photo uploaded');
              }
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed');
        }
        setImageUploading(prev => ({ ...prev, [editingIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [editingIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-leadership-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [editingIndex]: false }));
    }
  };

  const handleSubmitItem = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const itemIndex = editingIndex;
    const item = modalData;
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.name || !item.role || !item.bio) {
      toast.warning('Please fill all required fields');
      return;
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `items.${itemIndex}.update`,
            field_name: `Update Leadership Profile - ${item.name}`,
            old_value: originalData.items[itemIndex] || {},
            new_value: item,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${item.name} submitted for approval`);
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((c, i) => i === itemIndex ? item : c)
        }));
        setOriginalData(prev => ({
          ...prev,
          items: prev.items.map((c, i) => i === itemIndex ? item : c)
        }));
        setEditingIndex(null);
        setModalData(null);
      } else {
        if (response.status === 409) {
          toast.error('This profile already has a pending approval');
        } else {
          toast.error(result.error || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleSubmitItemDelete = async (itemIndex, item) => {
    if (!sectionId) { toast.error('Section ID not loaded'); return; }

    setSubmitting(prev => ({ ...prev, [`del_${itemIndex}`]: true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${itemIndex}.delete`,
          field_name: `Delete Leadership Profile - ${item.name}`,
          old_value: item,
          new_value: null,
          locale: 'en'
        })
      });
      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${item.name} deletion submitted for approval`);
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_${itemIndex}`]: false }));
    }
  };

  if (loading) return <div className="leadership-form-container"><div className="loading">Loading Leadership Team...</div></div>;

  return (
    <div className="leadership-form-container">
      <ToastContainer />
      <div className="leadership-form">
        <div className="form-header">
          <h2>Leadership Team</h2>
          <p className="form-description">Manage leadership team members and their profiles</p>
        </div>

        <div className="section-header">
          <h3>Team Members</h3>
          <button type="button" onClick={handleAddItem} className="add-item-btn">+ Add Member</button>
        </div>

        {formData.items.length === 0 ? (
          <div className="empty-state"><p>No team members yet. Click "Add Member" to create one.</p></div>
        ) : (
          <div className="cards-grid">
            {formData.items.map((item, idx) => (
              <div key={idx} className="team-card" onClick={() => {
                setEditingIndex(idx);
                setModalData({...item});
              }}>
                {item.photo && mediaMap[item.photo] && (
                  <div className="card-photo">
                    <img src={mediaMap[item.photo]} alt={item.name} />
                  </div>
                )}
                <div className="card-content">
                  <h4 className="card-name">{item.name}</h4>
                  <p className="card-role">{item.role}</p>
                  <p className="card-bio">{item.bio}</p>
                </div>
                <button type="button" onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(idx);
                }} className="card-delete">✕</button>
              </div>
            ))}
          </div>
        )}

        {/* EDIT MODAL */}
        {editingIndex !== null && modalData && (
          <div className="modal-overlay" onClick={() => {
            setEditingIndex(null);
            setModalData(null);
          }}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Team Member</h3>
                <button className="modal-close" onClick={() => {
                  setEditingIndex(null);
                  setModalData(null);
                }}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Name *</label>
                  <input type="text" value={modalData.name || ''} onChange={(e) => handleItemChange('name', e.target.value)} placeholder="Full name..." className="text-input" />
                </div>

                <div className="modal-field">
                  <label>Role *</label>
                  <input type="text" value={modalData.role || ''} onChange={(e) => handleItemChange('role', e.target.value)} placeholder="Job title..." className="text-input" />
                </div>

                <div className="modal-field">
                  <label>Bio *</label>
                  <textarea value={modalData.bio || ''} onChange={(e) => handleItemChange('bio', e.target.value)} placeholder="Biography..." className="textarea-input" rows="3" />
                </div>

                <div className="modal-field">
                  <label>Photo</label>
                  {modalData.photo && mediaMap[modalData.photo] && (
                    <div className="modal-photo-preview">
                      <img src={mediaMap[modalData.photo]} alt="Team member" />
                    </div>
                  )}
                  <div className="image-upload-area">
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} disabled={imageUploading[editingIndex]} className="image-input" id="modal-photo-input" />
                    <label htmlFor="modal-photo-input" className={`upload-label ${imageUploading[editingIndex] ? 'uploading' : ''}`}>
                      {imageUploading[editingIndex] ? `Uploading ${imageProgress[editingIndex]}%...` : 'Upload Photo'}
                    </label>
                  </div>
                  <span className="hint">Max 5MB • Recommended: 288×288px • JPG, PNG, WebP</span>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => {
                  setEditingIndex(null);
                  setModalData(null);
                }}>Cancel</button>
                <button className="btn-submit" disabled={submitting[`item_${editingIndex}`] || !modalData.name || !modalData.role || !modalData.bio} onClick={handleSubmitItem}>
                  {submitting[`item_${editingIndex}`] ? 'Submitting...' : 'Save & Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
